import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastTypeWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSaving: boolean;
  error: string | null;
  initialRoastType: string | null;
  roastType: string;
  roastTypeLabelMap: Record<string, string>;
}

/**
 * Confirmation dialog shown before persisting a roast-type change on a completed work order.
 * Presentational only — the caller owns the async save via `onConfirm`.
 */
export function RoastTypeWarningModal({
  isOpen,
  onClose,
  onConfirm,
  isSaving,
  error,
  initialRoastType,
  roastType,
  roastTypeLabelMap,
}: RoastTypeWarningModalProps) {
  const { t } = useLanguage();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (isSaving) return;
        onClose();
      }}
      title={t('workOrders.roastTypeEdit.warningTitle')}
      maxWidth="md"
    >
      <div className="space-y-4" data-test-id="wo-roast-type-warning-modal">
        <p className="text-sm text-gray-700">
          {t('workOrders.roastTypeEdit.warningMessage')}
        </p>
        <div className="rounded-md border border-primary-200 bg-primary-50 p-3 text-xs text-primary-900">
          <p className="font-semibold uppercase tracking-wider">
            {t('workOrders.roastTypeEdit.scopeHeader')}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            <li>{t('workOrders.roastTypeEdit.scopeOnlyRoastType')}</li>
            <li>{t('workOrders.roastTypeEdit.scopeStockUnchanged')}</li>
          </ul>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <span className="text-gray-500">{t('workOrders.table.roastType')}</span>
          <span className="flex items-center gap-2 font-medium text-gray-900">
            <span className="text-gray-500 line-through">
              {initialRoastType
                ? (roastTypeLabelMap[initialRoastType] ?? initialRoastType)
                : '—'}
            </span>
            <span aria-hidden="true" className="text-primary-600">→</span>
            <span className="rounded bg-primary-600 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-white">
              {roastTypeLabelMap[roastType] ?? roastType}
            </span>
          </span>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-test-id="wo-roast-type-warning-error">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              if (isSaving) return;
              onClose();
            }}
            disabled={isSaving}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-test-id="wo-roast-type-warning-cancel"
          >
            {t('workOrders.roastTypeEdit.backToEdit')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
            data-test-id="wo-roast-type-warning-confirm"
          >
            {isSaving
              ? t('workOrders.roastTypeEdit.saving')
              : t('workOrders.roastTypeEdit.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
